import { Component, OnInit, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import firebase from 'firebase';
import { environment } from 'src/environments/environment';

interface UserVehicle {
  id?: string;
  user_id?: string;
  owner_id?: string;
  plate_number: string;
  vehicle_type: string;
  vehicle_model?: string;
  vehicle_color?: string;
  vehicle_type_custom_name?: string;
  vin_number?: string;
  code?: string;
  latest_log_id?: string;
  previous_owner_id?: string | null;
  deleted?: boolean;
  
  // Owner information (for registration forms)
  owner_full_name?: string;
  owner_contact_number?: string;
  owner_email?: string;
  owner_address?: string;
  driver_license_number?: string;
  license_expiry_date?: string;
  
  // Document files/URLs
  cr_document?: File | string;
  or_document?: File | string;
  government_id?: File | string;
  drivers_license?: File[] | string[];
  insurance_document?: File | string;
  
  // Timestamps
  date_created?: Date | any;
  date_registered?: Date | any;
  updated_at?: Date | any;
  updatedAt?: Date | any;
  
  // Verification status
  verification_status?: 'pending' | 'verified' | 'rejected' | string;
  rejection_reason?: string;
  verified_by?: string;
  verified_date?: Date | any;
}

interface RegistrationStep {
  title: string;
  description: string;
}

@Component({
  selector: 'app-vehicle-registration-wizard',
  templateUrl: './vehicle-registration-wizard.component.html',
  styleUrls: ['./vehicle-registration-wizard.component.css']
})
export class VehicleRegistrationWizardComponent implements OnInit, OnChanges {

  @Input() user: any;
  @Input() isVisible: boolean = false;
  @Input() showAsFullScreen: boolean = false; // New input for full-screen mode
  @Input() demoMode: boolean = false; // Demo mode flag for simplified flow
  @Output() onCancel = new EventEmitter<void>();
  @Output() onSuccess = new EventEmitter<UserVehicle>();
  @Output() onError = new EventEmitter<string>();

  // Multi-step form properties
  currentStep: number = 1;
  declarationAccepted: boolean = false;
  registrationSteps: RegistrationStep[] = [
    { title: 'Vehicle Details', description: 'Basic vehicle information' },
    { title: 'Owner Details', description: 'Owner information as per documents' },
    { title: 'Driver Verification', description: 'Driver license information' },
    { title: 'Document Uploads', description: 'Required documents' },
    { title: 'Declaration', description: 'Review and confirmation' },
    { title: 'Status', description: 'Registration complete' }
  ];

  newVehicle: UserVehicle = {
    plate_number: '',
    vehicle_type: '',
    vehicle_model: '',
    vehicle_color: '',
    vin_number: '',
    owner_full_name: '',
    owner_contact_number: '',
    owner_email: '',
    owner_address: '',
    driver_license_number: '',
    license_expiry_date: '',
    drivers_license: []
  };

  isSubmittingVehicle: boolean = false;
  vehicleError: string = '';

  // Document thumbnail properties
  documentPreviews: { [key: string]: string | string[] } = {};

  constructor(
    private http: HttpClient
  ) { }

  ngOnInit() {
    this.resetForm();
    // Populate contact number from authenticated user's phone number
    this.populateUserContactNumber();
  }

  ngOnChanges() {
    if (this.isVisible) {
      this.resetForm();
      // Populate contact number from authenticated user's phone number
      this.populateUserContactNumber();
    }
  }

  resetForm() {
    this.currentStep = 1;
    this.declarationAccepted = false;
    this.newVehicle = {
      plate_number: '',
      vehicle_type: '',
      vehicle_model: '',
      vehicle_color: '',
      vin_number: '',
      owner_full_name: '',
      owner_contact_number: '',
      owner_email: '',
      owner_address: '',
      driver_license_number: '',
      license_expiry_date: '',
      drivers_license: []
    };
    this.documentPreviews = {};
    this.vehicleError = '';
    this.isSubmittingVehicle = false;
  }

  populateUserContactNumber() {
    // Populate contact number from authenticated user's phone number
    if (this.user && this.user.phoneNumber) {
      this.newVehicle.owner_contact_number = this.user.phoneNumber;
    }
  }

  cancelRegistration() {
    // Signal that vehicle checking should be cancelled
    localStorage.setItem('cancelVehicleCheck', 'true');
    
    this.resetForm();
    this.onCancel.emit();
  }

  cleanVehiclePlateNumber(event: any) {
    // Remove all special characters, spaces, and keep only letters and numbers
    const cleaned = event.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    this.newVehicle.plate_number = cleaned;
    
    // Clear errors when user starts typing
    if (this.vehicleError && this.vehicleError.includes('plate')) {
      this.vehicleError = '';
    }
  }

  // Validation methods
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  validatePhoneNumber(phone: string): boolean {
    // Philippine phone number validation (09XXXXXXXXX or +639XXXXXXXXX)
    const phoneRegex = /^(\+639|09)\d{9}$/;
    return phoneRegex.test(phone);
  }

  validateLicenseNumber(license: string): boolean {
    // Philippine license format validation (flexible)
    return license && license.length >= 8 && license.length <= 15;
  }

  validateLicenseExpiry(date: string): boolean {
    if (!date) return false;
    const expiryDate = new Date(date);
    const today = new Date();
    return expiryDate > today;
  }

  getValidationErrors(): string[] {
    const errors: string[] = [];
    
    switch (this.currentStep) {
      case 1:
        if (!this.newVehicle.plate_number) {
          errors.push('Plate number is required');
        } else if (this.newVehicle.plate_number.length < 6) {
          errors.push('Plate number must be at least 6 characters');
        }
        
        if (!this.newVehicle.vehicle_type) {
          errors.push('Vehicle type is required');
        }
        
        if (!this.newVehicle.vehicle_model) {
          errors.push('Vehicle model is required');
        }
        
        if (!this.newVehicle.vehicle_color) {
          errors.push('Vehicle color is required');
        }
        break;
        
      case 2:
        if (!this.newVehicle.owner_full_name) {
          errors.push('Owner full name is required');
        }
        
        if (!this.newVehicle.owner_contact_number) {
          errors.push('Contact number is required');
        } else if (!this.validatePhoneNumber(this.newVehicle.owner_contact_number)) {
          errors.push('Please enter a valid Philippine phone number (09XXXXXXXXX)');
        }
        
        if (!this.newVehicle.owner_email) {
          errors.push('Email address is required');
        } else if (!this.validateEmail(this.newVehicle.owner_email)) {
          errors.push('Please enter a valid email address');
        }
        
        if (!this.newVehicle.owner_address) {
          errors.push('Owner address is required');
        }
        break;
        
      case 3:
        if (!this.newVehicle.driver_license_number) {
          errors.push('Driver license number is required');
        } else if (!this.validateLicenseNumber(this.newVehicle.driver_license_number)) {
          errors.push('Please enter a valid license number');
        }
        
        if (!this.newVehicle.license_expiry_date) {
          errors.push('License expiry date is required');
        } else if (!this.validateLicenseExpiry(this.newVehicle.license_expiry_date)) {
          errors.push('License has expired or expiry date is invalid');
        }
        break;
        
      case 4:
        if (!this.newVehicle.cr_document) {
          errors.push('Certificate of Registration (CR) is required');
        }
        
        if (!this.newVehicle.or_document) {
          errors.push('Official Receipt (OR) is required');
        }
        
        if (!this.newVehicle.government_id) {
          errors.push('Valid Government ID is required');
        }
        
        if (!this.newVehicle.drivers_license || this.newVehicle.drivers_license.length === 0) {
          errors.push('Driver\'s License (front & back) is required');
        }
        break;
        
      case 5:
        if (!this.declarationAccepted) {
          errors.push('You must accept the declaration to proceed');
        }
        break;
    }
    
    return errors;
  }

  showValidationErrors(): boolean {
    const errors = this.getValidationErrors();
    if (errors.length > 0) {
      this.vehicleError = errors.join('. ');
      return false;
    }
    this.vehicleError = '';
    return true;
  }

  canProceedToNext(): boolean {
    switch (this.currentStep) {
      case 1:
        return !!(this.newVehicle.plate_number && 
                 this.newVehicle.vehicle_type && 
                 this.newVehicle.vehicle_model && 
                 this.newVehicle.vehicle_color);
      case 2:
        return !!(this.newVehicle.owner_full_name && 
                 this.newVehicle.owner_contact_number && 
                 this.newVehicle.owner_email && 
                 this.newVehicle.owner_address);
      case 3:
        return !!(this.newVehicle.driver_license_number && 
                 this.newVehicle.license_expiry_date);
      case 4:
        return !!(this.newVehicle.cr_document && 
                 this.newVehicle.or_document && 
                 this.newVehicle.government_id && 
                 this.newVehicle.drivers_license && 
                 this.newVehicle.drivers_license.length > 0);
      case 5:
        return this.declarationAccepted;
      default:
        return false;
    }
  }

  nextStep() {
    if (this.showValidationErrors()) {
      if (this.currentStep < this.registrationSteps.length) {
        this.currentStep++;
      }
    }
  }

  previousStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.vehicleError = ''; // Clear errors when going back
    }
  }

  handleStepSubmit() {
    if (this.currentStep === 5) {
      // Final submission - validate all previous steps
      if (this.showValidationErrors()) {
        this.registerVehicle();
      }
    } else if (this.demoMode && this.currentStep === 1) {
      // Demo mode: submit directly after step 1
      if (this.showValidationErrors()) {
        this.registerVehicleDemo();
      }
    } else {
      // Move to next step with validation
      this.nextStep();
    }
  }

  // File upload methods
  handleFileUpload(event: any, documentType: string) {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Clear any previous errors
    this.vehicleError = '';

    if (documentType === 'drivers_license') {
      // Handle multiple files for driver's license
      const validFiles: File[] = [];
      const previews: string[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Validate file
        if (this.validateFile(file)) {
          validFiles.push(file);
          this.generatePreview(file).then(preview => {
            previews.push(preview);
          });
        }
      }

      if (validFiles.length > 0) {
        this.newVehicle.drivers_license = validFiles;
        Promise.all(validFiles.map(file => this.generatePreview(file))).then(previewUrls => {
          this.documentPreviews[documentType] = previewUrls;
        });
      }
    } else {
      // Handle single file
      const file = files[0];
      
      if (!this.validateFile(file)) {
        return;
      }

      // Generate preview
      this.generatePreview(file).then(previewUrl => {
        this.documentPreviews[documentType] = previewUrl;
      });

      // Store file based on document type
      switch (documentType) {
        case 'cr_document':
          this.newVehicle.cr_document = file;
          break;
        case 'or_document':
          this.newVehicle.or_document = file;
          break;
        case 'government_id':
          this.newVehicle.government_id = file;
          break;
        case 'insurance_document':
          this.newVehicle.insurance_document = file;
          break;
      }
    }
  }

  private validateFile(file: File): boolean {
    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      this.vehicleError = 'File size must be less than 5MB';
      return false;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      this.vehicleError = 'Only JPG, PNG, and PDF files are allowed';
      return false;
    }

    return true;
  }

  private generatePreview(file: File): Promise<string> {
    return new Promise((resolve) => {
      if (file.type === 'application/pdf') {
        // For PDF files, use a PDF icon
        resolve('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1MTIgNTEyIj48cGF0aCBkPSJNMzIwIDBoLTI1NkMzNy41IDAgMTcuMiAyMC4zIDE3LjIgNDUuM3Y0MjEuNEMxNy4yIDQ5MS43IDM3LjUgNTEyIDY0IDUxMmgzODRjMjYuNSAwIDQ4LTIxLjUgNDgtNDhWMTQ0TDMyMCAwem0wIDEyOGgxMjhMMzIwIDE0NFYxMjh6Ii8+PC9zdmc+');
      } else {
        // For image files, create a thumbnail
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      }
    });
  }

  removeDocument(documentType: string, index?: number) {
    if (documentType === 'drivers_license' && typeof index === 'number') {
      // Remove specific driver's license file
      if (this.newVehicle.drivers_license) {
        this.newVehicle.drivers_license.splice(index, 1);
        const previews = this.documentPreviews[documentType] as string[];
        if (previews) {
          previews.splice(index, 1);
          this.documentPreviews[documentType] = previews;
        }
        if (this.newVehicle.drivers_license.length === 0) {
          delete this.newVehicle.drivers_license;
          delete this.documentPreviews[documentType];
        }
      }
    } else {
      // Remove single document
      switch (documentType) {
        case 'cr_document':
          delete this.newVehicle.cr_document;
          break;
        case 'or_document':
          delete this.newVehicle.or_document;
          break;
        case 'government_id':
          delete this.newVehicle.government_id;
          break;
        case 'insurance_document':
          delete this.newVehicle.insurance_document;
          break;
      }
      delete this.documentPreviews[documentType];
    }
    
    // Clear validation errors when removing documents
    this.vehicleError = '';
  }

  getUploadedDocumentCount(): number {
    let count = 0;
    
    // Count required documents
    if (this.documentPreviews['cr_document']) count++;
    if (this.documentPreviews['or_document']) count++;
    if (this.documentPreviews['government_id']) count++;
    if (this.documentPreviews['drivers_license'] && (this.documentPreviews['drivers_license'] as string[])?.length > 0) {
      count += (this.documentPreviews['drivers_license'] as string[]).length;
    }
    
    // Count optional documents
    if (this.documentPreviews['insurance_document']) count++;
    
    return count;
  }

  // Helper methods for template conditions
  hasDriversLicenseUploads(): boolean {
    return this.documentPreviews['drivers_license'] && 
           (this.documentPreviews['drivers_license'] as string[])?.length > 0;
  }

  getDriversLicensePreviews(): string[] {
    return (this.documentPreviews['drivers_license'] as string[]) || [];
  }

  async registerVehicle() {
    if (!this.user || !this.user.uid) {
      this.vehicleError = 'Authentication required. Please log in again.';
      this.onError.emit(this.vehicleError);
      return;
    }

    if (!this.canProceedToNext()) {
      this.vehicleError = 'Please complete all required fields and upload all required documents.';
      return;
    }

    this.isSubmittingVehicle = true;
    this.vehicleError = '';

    try {
      // Upload documents first (if any)
      const uploadedDocuments = await this.uploadVehicleDocuments();

      // Use Cloud Function to create vehicle with enhanced data
      const response: any = await this.http.post(`${environment.apiBaseUrl}/createUserVehicle`, {
        owner_id: this.user.uid,
        plateNumber: this.newVehicle.plate_number,
        vehicleType: this.newVehicle.vehicle_type,
        vehicleModel: this.newVehicle.vehicle_model,
        vehicleColor: this.newVehicle.vehicle_color,
        vinNumber: this.newVehicle.vin_number,
        ownerFullName: this.newVehicle.owner_full_name,
        ownerContactNumber: this.newVehicle.owner_contact_number,
        ownerEmail: this.newVehicle.owner_email,
        ownerAddress: this.newVehicle.owner_address,
        driverLicenseNumber: this.newVehicle.driver_license_number,
        licenseExpiryDate: this.newVehicle.license_expiry_date,
        uploadedDocuments: uploadedDocuments
      }).toPromise();

      if (response.success) {
        // Move to success step
        this.currentStep = 6;
        
        // Emit success event with the new vehicle data
        const newVehicle = {
          ...response.vehicle,
          date_registered: new Date(response.vehicle.date_registered)
        };
        
        this.onSuccess.emit(newVehicle);
        
        console.log('Vehicle registered successfully, pending verification');
      } else {
        this.vehicleError = response.message || 'Failed to register vehicle.';
        this.onError.emit(this.vehicleError);
      }
      
    } catch (error: any) {
      console.error('Error registering vehicle:', error);
      if (error.status === 409) {
        this.vehicleError = error.error.message || 'This plate number is already registered.';
      } else {
        this.vehicleError = 'Failed to register vehicle. Please try again.';
      }
      this.onError.emit(this.vehicleError);
    } finally {
      this.isSubmittingVehicle = false;
    }
  }

  private async uploadVehicleDocuments(): Promise<any> {
    const uploadedDocuments: any = {};
    
    try {
      if (this.newVehicle.cr_document) {
        if (this.newVehicle.cr_document instanceof File) {
          const crUrl = await this.uploadFileToStorage(this.newVehicle.cr_document, 'cr_documents');
          uploadedDocuments.cr_document = crUrl;
        } else {
          uploadedDocuments.cr_document = this.newVehicle.cr_document;
        }
      }
      
      if (this.newVehicle.or_document) {
        if (this.newVehicle.or_document instanceof File) {
          const orUrl = await this.uploadFileToStorage(this.newVehicle.or_document, 'or_documents');
          uploadedDocuments.or_document = orUrl;
        } else {
          uploadedDocuments.or_document = this.newVehicle.or_document;
        }
      }
      
      if (this.newVehicle.government_id) {
        if (this.newVehicle.government_id instanceof File) {
          const govIdUrl = await this.uploadFileToStorage(this.newVehicle.government_id, 'government_ids');
          uploadedDocuments.government_id = govIdUrl;
        } else {
          uploadedDocuments.government_id = this.newVehicle.government_id;
        }
      }
      
      if (this.newVehicle.drivers_license && this.newVehicle.drivers_license.length > 0) {
        const licenseUrls = [];
        for (let i = 0; i < this.newVehicle.drivers_license.length; i++) {
          const license = this.newVehicle.drivers_license[i];
          if (license instanceof File) {
            const licenseUrl = await this.uploadFileToStorage(license, 'drivers_licenses');
            licenseUrls.push(licenseUrl);
          } else {
            licenseUrls.push(license);
          }
        }
        uploadedDocuments.drivers_license = licenseUrls;
      }
      
      if (this.newVehicle.insurance_document) {
        if (this.newVehicle.insurance_document instanceof File) {
          const insuranceUrl = await this.uploadFileToStorage(this.newVehicle.insurance_document, 'insurance_documents');
          uploadedDocuments.insurance_document = insuranceUrl;
        } else {
          uploadedDocuments.insurance_document = this.newVehicle.insurance_document;
        }
      }
      
      return uploadedDocuments;
    } catch (error) {
      console.error('Error uploading documents:', error);
      throw new Error('Failed to upload documents. Please try again.');
    }
  }

  private async uploadFileToStorage(file: File, folder: string): Promise<string> {
    const storageRef = firebase.storage().ref();
    const fileRef = storageRef.child(`vehicle_documents/${this.user.uid}/${folder}/${Date.now()}_${file.name}`);
    
    const snapshot = await fileRef.put(file);
    return await snapshot.ref.getDownloadURL();
  }

  // Simplified demo registration function
  async registerVehicleDemo() {
    if (!this.user || !this.user.uid) {
      this.vehicleError = 'Authentication required. Please log in again.';
      this.onError.emit(this.vehicleError);
      return;
    }

    this.isSubmittingVehicle = true;
    this.vehicleError = '';

    try {
      // Create a simplified vehicle record directly in Firestore for demo
      const vehicleData = {
        owner_id: this.user.uid,
        user_id: this.user.uid,
        plate_number: this.newVehicle.plate_number,
        vehicle_type: this.newVehicle.vehicle_type,
        vehicle_model: this.newVehicle.vehicle_model,
        vehicle_color: this.newVehicle.vehicle_color,
        vin_number: this.newVehicle.vin_number || '',
        verification_status: 'verified', // Auto-approve for demo
        date_created: firebase.firestore.FieldValue.serverTimestamp(),
        date_registered: firebase.firestore.FieldValue.serverTimestamp(),
        verified_date: firebase.firestore.FieldValue.serverTimestamp(),
        verified_by: 'demo_system',
        // Set minimal required fields for demo
        owner_full_name: this.newVehicle.owner_full_name || 'Demo User',
        owner_contact_number: this.user.phoneNumber || '09123456789',
        owner_email: this.user.email || 'demo@example.com',
        owner_address: 'Demo Address',
        driver_license_number: 'DEMO-LICENSE-123',
        license_expiry_date: '2025-12-31'
      };

      // Add vehicle directly to Firestore
      const docRef = await firebase.firestore()
        .collection('vehicles')
        .add(vehicleData);

      // Create response object similar to the cloud function
      const newVehicle = {
        id: docRef.id,
        ...vehicleData,
        date_registered: new Date()
      };

      // Move to success step
      this.currentStep = 6;
      
      // Emit success event
      this.onSuccess.emit(newVehicle);
      
      console.log('Demo vehicle registered successfully');

    } catch (error: any) {
      console.error('Error registering demo vehicle:', error);
      if (error.code === 'permission-denied') {
        this.vehicleError = 'Permission denied. Please check your authentication.';
      } else {
        this.vehicleError = 'Failed to register vehicle. Please try again.';
      }
      this.onError.emit(this.vehicleError);
    } finally {
      this.isSubmittingVehicle = false;
    }
  }
}